/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Slobodan Djordjevic
 */
import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useLocation, useParams } from "react-router-dom"
import {
    Element,
    Row,
    Chip,
    Label,
    Color,
    Icon,
    CloseButton,
    Button,
    RoundButton,
    isSmallMobileScreen,
    VideoControlButton,
} from "trux"
import * as links from "../util/links"
import { SelectCameraDialog } from "../modules/SelectCameraDialog"
import { SelectInningDialog } from "../modules/SelectInningDialog"
import { SelectFilterDialog } from "./SelectFilterDialog"
import { buildFilterOptions, getAvailableFilters } from "../util/playlistStore"
import "./PanelTitle.css"

export const PanelTitle = ({
    currentPlaylist,
    currentEvent,
    events,
    allCameras,
    filterCameras,
    filterHalves,
    filterInnings,
    title,
    userId,
    hideFilters,
    ...props
}) => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()

    const selectedCamera = useSelector((state) => state.selectedCamera)
    const selectedHalf = useSelector((state) => state.selectedHalf)
    const inning = useSelector((state) => state.selectedInning)
    const baseballSide = useSelector((state) => state.selectedBaseballSide)
    const baseballTag = useSelector((state) => state.selectedBaseballTag)

    const [selectBaseballSideModalOpen, setSelectBaseballSideModalOpen] =
        useState(false)
    const [selectBaseballTagModalOpen, setSelectBaseballTagModalOpen] =
        useState(false)
    const [selectCameraModalOpen, setSelectCameraModalOpen] = useState(false)
    const [selectHalfModalOpen, setSelectHalfModalOpen] = useState(false)
    const [selectInningModalOpen, setSelectInningModalOpen] = useState(false)
    const allInnings = useSelector((state) => state.allInnings)
    const innings = Object.keys(allInnings)
    const sportType = useSelector((state) => state.game?.value?.sport_type)
    let { team_name, game_id, playlist_id, event_number } = useParams()

    const onClosePressed = (e) => {
        if (e) {
            e.preventDefault()
        }

        if (props.onClosePressed) {
            props.onClosePressed()
        }
    }

    const selectPlaylist = () => {
        let newPath = location.pathname.replace("/moments", "") + "/choose"
        return history.push(newPath)
    }

    const goToTraceId = () => {
        let newPath = ""
        if (userId) {
            newPath = links.traceIdUrl(`/find/user/${userId}`)
        } else {
            newPath = links.traceIdUrl(
                `/find/user/${currentPlaylist?.scope?.player?.user?.user_id}`
            )
        }
        // FIX: there must be a better way to do this
        Object.assign(document.createElement("a"), {
            target: "_blank",
            href: newPath,
        }).click()
    }

    const getHalfChipContent = (half) => {
        if (half === 0) {
            return "Both halves"
        } else if (half === 1) {
            return "1st half"
        } else if (half === 2) {
            return "2nd half"
        }
    }

    const getCameraChipContent = (camera) => {
        let content = "All cameras"
        for (let c in allCameras) {
            c = allCameras[c]
            if (c.name === camera) {
                content = c.title
                break
            }
        }
        return content
    }

    let playlistTitle = ""

    if (currentPlaylist) {
        if (
            currentPlaylist.scopes[0] === "player" &&
            currentPlaylist.scope.player.jersey_number !== ""
        ) {
            playlistTitle = `#${currentPlaylist.scope.player.jersey_number} ${currentPlaylist.title}`
        } else {
            playlistTitle = `${currentPlaylist.title}`
        }
    }

    const modalRootNode = document.getElementById("modal-root")

    return (
        <>
            <Element className="PanelTitle">
                <Row className="playlist-title-info">
                    {!title && (
                        <>
                            <Element
                                data-trk-act="Choose Playlist Pressed"
                                className={`playlist-selector ${
                                    props.clipViewsEnabled
                                        ? "with-clips"
                                        : "without-clips"
                                }`}
                            >
                                {currentPlaylist && (
                                    <Row
                                        alignItems="center"
                                        spacingTop="4"
                                        spacingBottom="4"
                                        spacingLeft="4"
                                        className="title-top"
                                    >
                                        {!isSmallMobileScreen() && (
                                            <CloseButton
                                                onClick={onClosePressed}
                                            />
                                        )}
                                        <Row
                                            className="playlist-name-selector"
                                            alignItems="center"
                                        >
                                            <Label
                                                className="playlist-name"
                                                type={Label.Type.Heading4}
                                                bold
                                                color={Color.Trace}
                                                onClick={selectPlaylist}
                                            >
                                                {playlistTitle}&nbsp;
                                            </Label>
                                            <Label
                                                type={Label.Type.Heading4}
                                                bold
                                                color={Color.Trace}
                                                onClick={selectPlaylist}
                                                content={`${
                                                    currentEvent?.display_number
                                                        ? currentEvent.display_number
                                                        : 1
                                                }/${events?.length}`}
                                            />
                                            <Icon
                                                size="24"
                                                type={Icon.Type.CheveronDown}
                                                color={Color.Trace}
                                                onClick={selectPlaylist}
                                            />
                                            <VideoControlButton
                                                trkAction="Players Pressed"
                                                icon={Icon.Type.PlayersGeneric}
                                                iconColor={Color.Trace}
                                                onClick={(e) =>
                                                    history.push(
                                                        links.gamePlayersUrl(
                                                            team_name,
                                                            playlist_id,
                                                            parseInt(
                                                                game_id,
                                                                10
                                                            ),
                                                            event_number
                                                        )
                                                    )
                                                }
                                                iconSize="24"
                                                hideSM
                                                title="Players"
                                                className="playlist-player-icon hide-md hide-lg"
                                            />
                                            <VideoControlButton
                                                trkAction="More Pressed"
                                                icon={Icon.Type.More}
                                                iconColor={Color.Trace}
                                                onClick={(e) =>
                                                    history.push(
                                                        links.gameOptionsUrl(
                                                            team_name,
                                                            playlist_id,
                                                            parseInt(
                                                                game_id,
                                                                10
                                                            ),
                                                            event_number
                                                        )
                                                    )
                                                }
                                                iconSize="24"
                                                title="Moment options"
                                                className="playlist-more-icon hide-md hide-lg"
                                            />
                                        </Row>

                                        {currentPlaylist.scopes[0] ===
                                            "player" &&
                                            !currentPlaylist.title.includes(
                                                "Tracer"
                                            ) &&
                                            !isSmallMobileScreen() && (
                                                <Button
                                                    className="user-trace-id-button"
                                                    trkAction="PanelTitle - Go To Trace iD"
                                                    content={
                                                        <Row alignItems="center">
                                                            <Label
                                                                type={
                                                                    Label.Type
                                                                        .InnerButton
                                                                }
                                                                content={`${
                                                                    currentPlaylist.title.split(
                                                                        " "
                                                                    )[0]
                                                                }'s Trace iD`}
                                                                marginRight="2"
                                                            />
                                                            <Icon
                                                                size={16}
                                                                type={
                                                                    Icon.Type
                                                                        .Leave
                                                                }
                                                            />
                                                        </Row>
                                                    }
                                                    onClick={goToTraceId}
                                                    small
                                                    marginRight="4"
                                                />
                                            )}
                                    </Row>
                                )}
                                {!currentPlaylist && (
                                    <Label
                                        type={Label.Type.Heading4}
                                        bold
                                        color={Color.Trace}
                                        content="Playlist loading"
                                        spacingTop="6"
                                        spacingBottom="6"
                                        spacingLeft="4"
                                        className="title-top move-down"
                                    />
                                )}
                            </Element>
                        </>
                    )}
                    {title && (
                        <Row
                            alignItems="center"
                            spacingTop="4"
                            spacingBottom="4"
                            spacingLeft="4"
                            className="title-top"
                        >
                            {!userId && (
                                <CloseButton onClick={onClosePressed} />
                            )}
                            {userId && (
                                <RoundButton
                                    trkAction="Back Pressed"
                                    className={"CloseButton"}
                                    icon={Icon.Type.CheveronLeft}
                                    color={Color.Transparent}
                                    backgroundColor={Color.Transparent}
                                    iconColor={Color.Grey3}
                                    iconSize={24}
                                    width={40}
                                    height={40}
                                    onClick={onClosePressed}
                                />
                            )}
                            <Label
                                type={Label.Type.Heading4}
                                bold
                                color={Color.Black}
                                content={title}
                                className="panel-players-title"
                            />

                            {userId && title && !title.includes("Tracer") && (
                                <Button
                                    className="user-trace-id-button"
                                    trkAction="PanelTitle - Go To Trace iD"
                                    content={
                                        <Row alignItems="center">
                                            <Label
                                                type={Label.Type.InnerButton}
                                                content={`${title}'s Trace iD`}
                                                marginRight="2"
                                            />
                                            <Icon
                                                size={16}
                                                type={Icon.Type.Leave}
                                            />
                                        </Row>
                                    }
                                    onClick={goToTraceId}
                                    small
                                    marginRight="4"
                                />
                            )}
                        </Row>
                    )}
                </Row>
                {!isSmallMobileScreen() && !hideFilters && (
                    <PlaylistFilters
                        allCameras={allCameras}
                        cameraChipContent={getCameraChipContent(selectedCamera)}
                        selectedCamera={selectedCamera}
                        setSelectCameraModalOpen={(value) =>
                            setSelectCameraModalOpen(value)
                        }
                        halfChipContent={getHalfChipContent(selectedHalf)}
                        selectedHalf={selectedHalf}
                        setSelectHalfModalOpen={(value) =>
                            setSelectHalfModalOpen(value)
                        }
                        selectedInning={inning}
                        setSelectInningModalOpen={(value) =>
                            setSelectInningModalOpen(value)
                        }
                        sportType={sportType}
                        selectedBaseballSide={baseballSide}
                        setSelectBaseballSideModalOpen={
                            setSelectBaseballSideModalOpen
                        }
                        selectedBaseballTag={baseballTag}
                        setSelectBaseballTagModalOpen={
                            setSelectBaseballTagModalOpen
                        }
                    />
                )}
                <Element className="PanelTitle-Top">
                    {isSmallMobileScreen() && (
                        <Element className="extras">
                            {props.userControls}
                            {!hideFilters && (
                                <PlaylistFilters
                                    allCameras={allCameras}
                                    cameraChipContent={getCameraChipContent(
                                        selectedCamera
                                    )}
                                    selectedCamera={selectedCamera}
                                    setSelectCameraModalOpen={(value) =>
                                        setSelectCameraModalOpen(value)
                                    }
                                    halfChipContent={getHalfChipContent(
                                        selectedHalf
                                    )}
                                    selectedHalf={selectedHalf}
                                    setSelectHalfModalOpen={(value) =>
                                        setSelectHalfModalOpen(value)
                                    }
                                    selectedInning={inning}
                                    setSelectInningModalOpen={(value) =>
                                        setSelectInningModalOpen(value)
                                    }
                                    sportType={sportType}
                                    selectedBaseballSide={baseballSide}
                                    setSelectBaseballSideModalOpen={
                                        setSelectBaseballSideModalOpen
                                    }
                                    selectedBaseballTag={baseballTag}
                                    setSelectBaseballTagModalOpen={
                                        setSelectBaseballTagModalOpen
                                    }
                                />
                            )}
                            {props.extras}
                        </Element>
                    )}
                </Element>
            </Element>
            {allCameras?.length > 1 && (
                <SelectCameraDialog
                    parent={modalRootNode}
                    modalOpen={selectCameraModalOpen}
                    setSelectCameraModalOpen={(value) =>
                        setSelectCameraModalOpen(value)
                    }
                    allCameras={allCameras}
                    selectedCamera={selectedCamera}
                    setSelectedCamera={filterCameras}
                />
            )}
            {sportType === "soccer" && !hideFilters && (
                <SelectFilterDialog
                    title={"Filter Halves"}
                    parent={modalRootNode}
                    open={selectHalfModalOpen}
                    setOpen={(value) => setSelectHalfModalOpen(value)}
                    value={selectedHalf}
                    onValueChanged={(value) =>
                        dispatch({
                            type: "SET_FILTER",
                            key: "selectedHalf",
                            value: value,
                        })
                    }
                    options={buildFilterOptions(
                        getAvailableFilters("half", events),
                        [
                            { title: "Both halves", value: 0 },
                            { title: "1st half", value: 1 },
                            { title: "2nd half", value: 2 },
                        ]
                    )}
                />
            )}
            {sportType === "baseball" && !hideFilters && (
                <>
                    <SelectFilterDialog
                        title={"Offense / Defense Filter"}
                        parent={modalRootNode}
                        open={selectBaseballSideModalOpen}
                        setOpen={(value) =>
                            setSelectBaseballSideModalOpen(value)
                        }
                        value={baseballSide}
                        onValueChanged={(value) =>
                            dispatch({
                                type: "SET_FILTER",
                                key: "selectedBaseballSide",
                                value: value,
                            })
                        }
                        options={buildFilterOptions(
                            getAvailableFilters("tags", events),
                            [
                                { title: "Off & Def", value: "all" },
                                { title: "Offense", value: "offensive" },
                                { title: "Defense", value: "defensive" },
                            ]
                        )}
                    />
                    <SelectFilterDialog
                        title={"Play Types"}
                        parent={modalRootNode}
                        open={selectBaseballTagModalOpen}
                        setOpen={setSelectBaseballTagModalOpen}
                        value={baseballTag}
                        onValueChanged={(value) =>
                            dispatch({
                                type: "SET_FILTER",
                                key: "selectedBaseballTag",
                                value: value,
                            })
                        }
                        options={buildFilterOptions(
                            getAvailableFilters("tags", events),
                            [
                                { title: "All Plays", value: "all" },
                                { title: "Hit", value: "hit" },
                                { title: "Single", value: "single" },
                                { title: "Double", value: "double" },
                                { title: "Triple", value: "triple" },
                                { title: "Homerun", value: "homerun" },
                                { title: "Out", value: "out" },
                                { title: "Pitcher", value: "pitcher" },
                            ]
                        )}
                    />
                    <SelectInningDialog
                        parent={modalRootNode}
                        modalOpen={selectInningModalOpen}
                        setSelectInningModalOpen={(value) =>
                            setSelectInningModalOpen(value)
                        }
                        selectedInning={inning}
                        setSelectedInning={(value) =>
                            dispatch({
                                type: "SET_FILTER",
                                key: "selectedInning",
                                value: value,
                            })
                        }
                        allInnings={innings}
                    />
                </>
            )}
        </>
    )
}

const PlaylistFilters = ({ sportType, ...props }) => {
    return (
        <Row className="playlist-filters">
            {props.allCameras?.length > 1 && (
                // removed playlist filter for now
                <Chip
                    hide
                    content={props.cameraChipContent}
                    icon={Icon.Type.CheveronDown}
                    background={
                        props.selectedCamera !== "all"
                            ? Color.Black
                            : Color.Grey4
                    }
                    color={
                        props.selectedCamera !== "all"
                            ? Color.White2
                            : Color.Black
                    }
                    icon_on_right={true}
                    fat={true}
                    className="Chip playlist-filter-chip"
                    onClick={() => props.setSelectCameraModalOpen(true)}
                    marginRight="2"
                />
            )}
            {sportType === "soccer" && (
                <Chip
                    content={props.halfChipContent}
                    icon={Icon.Type.CheveronDown}
                    background={
                        props.selectedHalf !== 0 ? Color.Black : Color.Grey4
                    }
                    color={
                        props.selectedHalf !== 0 ? Color.White2 : Color.Black
                    }
                    icon_on_right={true}
                    fat={true}
                    className="Chip playlist-filter-chip"
                    onClick={() => props.setSelectHalfModalOpen(true)}
                />
            )}
            {sportType === "baseball" && (
                <>
                    <Chip
                        content={
                            props.selectedBaseballSide === "all"
                                ? "Off & Def"
                                : props.selectedBaseballSide
                        }
                        icon={Icon.Type.CheveronDown}
                        background={
                            props.selectedBaseballSide !== "all"
                                ? Color.Black
                                : Color.Grey4
                        }
                        color={
                            props.selectedBaseballSide !== "all"
                                ? Color.White2
                                : Color.Black
                        }
                        icon_on_right={true}
                        fat={true}
                        className="Chip playlist-filter-chip"
                        onClick={() =>
                            props.setSelectBaseballSideModalOpen(true)
                        }
                    />
                    <Chip
                        content={
                            props.selectedBaseballTag === "all"
                                ? "All Plays"
                                : props.selectedBaseballTag
                        }
                        icon={Icon.Type.CheveronDown}
                        background={
                            props.selectedBaseballTag !== "all"
                                ? Color.Black
                                : Color.Grey4
                        }
                        color={
                            props.selectedBaseballTag !== "all"
                                ? Color.White2
                                : Color.Black
                        }
                        icon_on_right={true}
                        fat={true}
                        className="Chip playlist-filter-chip"
                        onClick={() =>
                            props.setSelectBaseballTagModalOpen(true)
                        }
                    />
                </>
            )}
        </Row>
    )
}
